import { Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { SidebarProps } from "../../sdk/theme";
import type { Folder, Label, MailboxAccess } from "../../shared/types";
import { emptyFolder } from "../actions";
import { api } from "../api";
import { avatarColor, initials } from "../format";
import { useAppPages } from "../modules";
import { sendOrQueue } from "../offline";
import { overridable } from "../overrides";
import {
  type MailSource,
  mailboxHome,
  mailHash,
  navigate,
  sourcePath,
  UNIFIED_INBOX,
  useHash,
} from "../router";
import {
  app,
  errorToast,
  mailChanged,
  refreshFolders,
  refreshSearches,
  toast,
  useFields,
} from "../store";
import { openContextMenu } from "./ContextMenu";
import { folderIcon, Icon, type IconName } from "./Icon";
import { IconButton, Menu, type MenuItem } from "./ui";

const sameSource = (a: MailSource | null, b: MailSource) =>
  a !== null &&
  a.kind === b.kind &&
  (a.kind === "search" || b.kind === "search" ? false : a.id === (b as { id: string }).id);

/** Drop target for dragging messages onto a folder or label. */
const dropProps = (onDrop: (ids: string[]) => void) => ({
  onDragOver: (event: DragEvent) => {
    if (event.dataTransfer?.types.includes("application/x-cloudmail-ids")) {
      event.preventDefault();
      (event.currentTarget as HTMLElement).classList.add("drop");
    }
  },
  onDragLeave: (event: DragEvent) => (event.currentTarget as HTMLElement).classList.remove("drop"),
  onDrop: (event: DragEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drop");
    const raw = event.dataTransfer?.getData("application/x-cloudmail-ids");
    if (raw) onDrop(JSON.parse(raw) as string[]);
  },
});

const NavItem = ({
  icon,
  label,
  count,
  active,
  href,
  depth = 0,
  color,
  bold,
  drop,
  actions,
  menu,
  extraClass,
}: {
  icon: IconName;
  label: string;
  count?: number;
  active: boolean;
  href: string;
  depth?: number;
  color?: string;
  bold?: boolean;
  drop?: (ids: string[]) => void;
  actions?: preact.ComponentChildren;
  /** Right-click menu. */
  menu?: () => MenuItem[];
  extraClass?: string;
}) => (
  <div
    class={`nav-item${active ? " active" : ""}${extraClass ? ` ${extraClass}` : ""}`}
    data-part="nav-item"
    {...(drop ? dropProps(drop) : {})}
    {...(menu ? { onContextMenu: (event: MouseEvent) => openContextMenu(event, menu()) } : {})}
  >
    <a
      href={href}
      class={bold && count ? "unread" : ""}
      style={{ paddingLeft: `${24 + depth * 14}px` }}
    >
      {color ? <span class="dot" style={{ background: color }} /> : <Icon name={icon} size={20} />}
      <span class="nav-label">{label}</span>
      {count !== undefined && count > 0 && <span class="count">{count}</span>}
    </a>
    {actions}
  </div>
);

const flatten = (folders: Folder[]) => {
  const byParent = new Map<string | null, Folder[]>();
  for (const folder of folders) {
    const key = folder.role ? null : folder.parentId;
    byParent.set(key, [...(byParent.get(key) ?? []), folder]);
  }
  const out: { folder: Folder; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const folder of byParent.get(parent) ?? []) {
      out.push({ folder, depth });
      walk(folder.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
};

export const moveTo = async (
  ids: string[],
  folderId: string,
  threads: boolean,
  scopeFolderId: string | null,
) => {
  try {
    await sendOrQueue("PATCH", "/messages", { ids, folderId, threads, scopeFolderId });
    mailChanged();
    const folder = app.get().folders.find((entry) => entry.id === folderId);
    // Undo puts them back in the folder they were dragged or moved out of.
    const from = scopeFolderId !== folderId ? scopeFolderId : null;
    toast(`Moved to ${folder?.name ?? "folder"}`, {
      ...(from
        ? {
            action: {
              label: "Undo",
              run: () => {
                void sendOrQueue("PATCH", "/messages", {
                  ids,
                  folderId: from,
                  threads,
                  scopeFolderId: null,
                  restoreSchedule: folder?.role === "trash",
                })
                  .then(mailChanged)
                  .catch(errorToast);
              },
            },
          }
        : {}),
    });
  } catch (error) {
    errorToast(error);
  }
};

const boxTitle = (box: MailboxAccess) =>
  box.kind === "person" ? box.name || "My mailbox" : box.name || box.address;

const BoxAvatar = ({ box }: { box: MailboxAccess }) => (
  <span class="avatar tiny" style={{ background: avatarColor(box.address) }} aria-hidden="true">
    {box.kind === "shared" ? (
      <Icon name="users" size={11} />
    ) : (
      initials({ name: box.name, address: box.address })
    )}
  </span>
);

/**
 * The account switcher at the top of the sidebar: the mailbox on screen, and a dropdown of the
 * person's own and shared mailboxes with their unread counts. "g then 1–9" picks one by number.
 */
const MailboxSwitcher = ({
  mailboxes,
  current,
  unified,
  allowUnified,
}: {
  mailboxes: MailboxAccess[];
  /** The mailbox on screen: null for the person's own. */
  current: string | null;
  /** Whether the unified inbox is on screen. */
  unified: boolean;
  allowUnified: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const isActive = (box: MailboxAccess) =>
    box.kind === "person" ? current === null : current === box.id;
  const active = mailboxes.find(isActive) ?? mailboxes[0];
  if (!active) return null;
  // Unread mail waiting in the mailboxes not on screen.
  const elsewhere = mailboxes
    .filter((box) => unified || !isActive(box))
    .reduce((sum, box) => sum + box.unread, 0);
  const go = (hash: string) => {
    setOpen(false);
    app.set({ navOpen: false });
    navigate(hash);
  };
  const identity = (
    <>
      {unified ? <Icon name="inbox" size={20} /> : <BoxAvatar box={active} />}
      <span class="switcher-text">
        <span class="switcher-name">{unified ? "All inboxes" : boxTitle(active)}</span>
        <span class="switcher-address">{unified ? "Mail from every mailbox" : active.address}</span>
      </span>
    </>
  );
  if (mailboxes.length < 2 && !unified)
    return (
      <div class="mailbox-switcher">
        <span class="switcher-label">Mailbox</span>
        <div class="switcher-button">{identity}</div>
      </div>
    );
  return (
    <div class="mailbox-switcher" ref={ref}>
      <span class="switcher-label">Mailbox</span>
      <button
        type="button"
        class="switcher-button"
        aria-expanded={open}
        aria-controls="mailbox-options"
        aria-label={
          unified
            ? "Viewing all inboxes. Switch mailbox"
            : `Viewing ${active.address}. Switch mailbox`
        }
        title="View one mailbox at a time (g then 1–9)"
        onClick={() => setOpen(!open)}
      >
        {identity}
        {elsewhere > 0 && !open && (
          <span class="count" title="Unread in your other mailboxes">
            {elsewhere}
          </span>
        )}
        <Icon name="chevronDown" size={15} />
      </button>
      {open && (
        <div class="switcher-menu" id="mailbox-options" role="group" aria-label="Choose a mailbox">
          {allowUnified && (
            <button
              type="button"
              aria-pressed={unified}
              class={unified ? "current" : ""}
              onClick={() => go(UNIFIED_INBOX)}
            >
              <Icon name="inbox" size={20} />
              <span class="switcher-text">
                <span class="switcher-name">All inboxes</span>
                <span class="switcher-address">Combine mail from every mailbox</span>
              </span>
              <kbd>0</kbd>
            </button>
          )}
          {mailboxes.map((box, index) => (
            <button
              type="button"
              aria-pressed={isActive(box) && !unified}
              key={box.id}
              class={isActive(box) && !unified ? "current" : ""}
              onClick={() => go(mailboxHome(box.kind === "person" ? null : box.id))}
            >
              <BoxAvatar box={box} />
              <span class="switcher-text">
                <span class="switcher-name">{boxTitle(box)}</span>
                <span class="switcher-address">
                  {box.address}
                  {box.role === "read" ? " · read only" : ""}
                </span>
              </span>
              {box.unread > 0 && <span class="count">{box.unread}</span>}
              {index < 9 && <kbd>{index + 1}</kbd>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DefaultSidebar = ({ current }: SidebarProps) => {
  const appPages = useAppPages();
  const hash = useHash();
  const filePath = /\/files(?:\/(.*))?$/.exec(hash);
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);
  const { folders, labels, counts, navOpen, me, mailbox, mailboxes, searches } = useFields(
    app,
    "folders",
    "labels",
    "counts",
    "navOpen",
    "me",
    "mailbox",
    "mailboxes",
    "searches",
  );
  const activeBox = mailbox ? mailboxes.find((entry) => entry.id === mailbox) : undefined;
  const unified = current?.kind === "view" && current.id === "inboxes";
  const [foldersOpen, setFoldersOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(true);
  const [searchesOpen, setSearchesOpen] = useState(true);
  const conversation = me?.settings.conversationView ?? true;
  const scope = current?.kind === "folder" ? current.id : null;
  const swipe = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (
      current?.kind === "folder" &&
      folders.some(
        (f) => f.id === current.id && f.role && !["inbox", "sent", "drafts"].includes(f.role),
      )
    )
      setMoreOpen(true);
    if (current?.kind === "view" && ["all", "vip", "scheduled", "snoozed"].includes(current.id))
      setMoreOpen(true);
  }, [current?.kind, current?.kind === "search" ? current.query : current?.id, folders]);
  const workspaceSelected = appPages.some(
    (page) => hash === (page.id === "files" ? mailHash("#/files") : (page.href ?? `#/${page.id}`)),
  );
  useEffect(() => {
    if (workspaceSelected) setWorkspaceOpen(true);
  }, [hash, workspaceSelected]);
  const closeNav = () => app.set({ navOpen: false });

  // Escape puts the mobile drawer away.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeNav();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const addFolder = async (parentId: string | null = null) => {
    const name = window.prompt("Folder name");
    if (!name?.trim()) return;
    try {
      await api.post("/folders", { name: name.trim(), parentId });
      await refreshFolders();
    } catch (error) {
      errorToast(error);
    }
  };

  const openSource = (source: MailSource): MenuItem => ({
    label: "Open",
    icon: "inbox",
    onSelect: () => navigate(sourcePath(source)),
  });
  const markFolderRead = (folder: Folder): MenuItem => ({
    label: "Mark all as read",
    icon: "mailOpen",
    onSelect: async () => {
      await api.post(`/folders/${folder.id}/read`).catch(errorToast);
      mailChanged();
    },
  });
  const systemMenu = (folder: Folder): MenuItem[] => [
    openSource({ kind: "folder", id: folder.id }),
    ...(folder.role === "sent" || folder.role === "drafts" ? [] : [markFolderRead(folder)]),
    ...(folder.role === "trash" || folder.role === "junk"
      ? [
          { separator: true as const },
          {
            label: `Empty ${folder.name}`,
            icon: "trash" as const,
            danger: true,
            onSelect: () => emptyFolder(folder),
          },
        ]
      : []),
  ];
  const folderMenu = (folder: Folder): MenuItem[] => [
    {
      label: "New subfolder",
      icon: "plus",
      onSelect: () => addFolder(folder.id),
    },
    {
      label: "Rename",
      icon: "pencil",
      onSelect: async () => {
        const name = window.prompt("Folder name", folder.name);
        if (!name?.trim()) return;
        await api.patch(`/folders/${folder.id}`, { name: name.trim() }).catch(errorToast);
        await refreshFolders();
      },
    },
    {
      label: "Mark all as read",
      icon: "mailOpen",
      onSelect: async () => {
        await api.post(`/folders/${folder.id}/read`).catch(errorToast);
        mailChanged();
      },
    },
    { separator: true },
    {
      label: "Delete folder",
      icon: "trash",
      danger: true,
      onSelect: async () => {
        if (
          !window.confirm(`Delete “${folder.name}” and its subfolders? Their mail moves to Trash.`)
        )
          return;
        await api.del(`/folders/${folder.id}`).catch(errorToast);
        if (sameSource(current, { kind: "folder", id: folder.id })) navigate("#/", true);
        mailChanged();
      },
    },
  ];
  const labelMenu = (label: Label): MenuItem[] => [
    {
      label: "Rename",
      icon: "pencil",
      onSelect: async () => {
        const name = window.prompt("Label name", label.name);
        if (!name?.trim()) return;
        await api.patch(`/labels/${label.id}`, { name: name.trim() }).catch(errorToast);
        await refreshFolders();
      },
    },
    ...[
      "#ef4444",
      "#f97316",
      "#eab308",
      "#22c55e",
      "#06b6d4",
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#6b7280",
    ].map((color) => ({
      label: color === label.color ? "Current color" : "Use this color",
      color,
      checked: color === label.color,
      onSelect: async () => {
        await api.patch(`/labels/${label.id}`, { color }).catch(errorToast);
        await refreshFolders();
      },
    })),
    { separator: true },
    {
      label: "Delete label",
      icon: "trash",
      danger: true,
      onSelect: async () => {
        if (!window.confirm(`Delete the label “${label.name}”? Messages keep their folders.`))
          return;
        await api.del(`/labels/${label.id}`).catch(errorToast);
        mailChanged();
      },
    },
  ];

  const addLabel = async () => {
    const name = window.prompt("Label name");
    if (!name?.trim()) return;
    const palette = [
      "#ef4444",
      "#f97316",
      "#eab308",
      "#22c55e",
      "#06b6d4",
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
    ];
    try {
      await api.post("/labels", {
        name: name.trim(),
        color: palette[labels.length % palette.length],
      });
      await refreshFolders();
    } catch (error) {
      errorToast(error);
    }
  };

  const system = folders.filter((folder) => folder.role !== null);
  const custom = flatten(folders.filter((folder) => folder.role === null));

  if (filePath && appPages.some((page) => page.id === "files"))
    return (
      <>
        <div class={`nav-scrim${navOpen ? " open" : ""}`} onClick={closeNav} />
        <nav
          class={`sidebar file-sidebar${navOpen ? " open" : ""}`}
          data-part="sidebar"
          aria-label="Files navigation"
          aria-hidden={mobile && !navOpen}
          inert={mobile && !navOpen}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a[href]")) closeNav();
          }}
        >
          {mailboxes.length > 0 && (
            <MailboxSwitcher
              mailboxes={mailboxes}
              current={mailbox}
              unified={false}
              allowUnified={false}
            />
          )}
          <div class="nav-section">
            <NavItem
              icon="folder"
              label="My files"
              active={!filePath[1] || filePath[1].startsWith("folder/")}
              href={mailHash("#/files")}
            />
            <NavItem
              icon="users"
              label="Shared with me"
              active={filePath[1]?.startsWith("shared") ?? false}
              href={mailHash("#/files/shared")}
            />
            <NavItem
              icon="trash"
              label="Trash"
              active={filePath[1] === "trash"}
              href={mailHash("#/files/trash")}
            />
            {me?.modules.includes("templates") && (
              <NavItem
                icon="image"
                label="Hosted images"
                active={filePath[1] === "images"}
                href={mailHash("#/files/images")}
              />
            )}
          </div>
          <div class="workspace-navigation" role="group" aria-label="Workspace navigation">
            <NavItem icon="mail" label="Back to mail" active={false} href={mailboxHome(mailbox)} />
            <NavItem
              icon="book"
              label="Contacts"
              active={false}
              href="#/contacts"
              extraClass="hide-mobile"
            />
            <NavItem
              icon="settings"
              label="Settings"
              active={false}
              href="#/settings/general"
              extraClass="hide-mobile"
            />
          </div>
        </nav>
      </>
    );
  return (
    <>
      <div class={`nav-scrim${navOpen ? " open" : ""}`} onClick={closeNav} />
      <nav
        class={`sidebar${navOpen ? " open" : ""}`}
        data-part="sidebar"
        aria-label="Mailboxes"
        aria-hidden={mobile && !navOpen}
        inert={mobile && !navOpen}
        // Tapping a link closes the drawer, even one to the page already on screen.
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("a[href]")) closeNav();
        }}
        // A swipe to the left closes it too.
        onTouchStart={(event) => {
          const touch = event.touches[0];
          swipe.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchEnd={(event) => {
          const start = swipe.current;
          const touch = event.changedTouches[0];
          swipe.current = null;
          if (!start || !touch || !navOpen) return;
          const dx = touch.clientX - start.x;
          if (dx < -60 && Math.abs(dx) > Math.abs(touch.clientY - start.y) * 1.5) closeNav();
        }}
      >
        {mailboxes.length > 0 && (
          <MailboxSwitcher
            mailboxes={mailboxes}
            current={mailbox}
            unified={unified}
            allowUnified={mailboxes.length > 1 && (me?.settings.unifiedInbox ?? false)}
          />
        )}
        {activeBox && (
          <div class="nav-section" data-part="nav-section">
            <NavItem
              icon="user"
              label="Assigned to me"
              active={sameSource(current, { kind: "view", id: "mine" })}
              href={sourcePath({ kind: "view", id: "mine" })}
            />
            <NavItem
              icon="inbox"
              label="Unassigned"
              active={sameSource(current, { kind: "view", id: "unassigned" })}
              href={sourcePath({ kind: "view", id: "unassigned" })}
            />
          </div>
        )}
        <div class="nav-section" data-part="nav-section">
          {system
            .filter((folder) => ["inbox", "sent", "drafts"].includes(folder.role ?? ""))
            .sort(
              (a, b) =>
                ["inbox", "sent", "drafts"].indexOf(a.role ?? "") -
                ["inbox", "sent", "drafts"].indexOf(b.role ?? ""),
            )
            .map((folder) => {
              const source: MailSource = { kind: "folder", id: folder.id };
              const showCount =
                folder.role === "drafts"
                  ? folder.total
                  : folder.role === "sent" || folder.role === "trash" || folder.role === "archive"
                    ? 0
                    : folder.unread;
              return (
                <Fragment key={folder.id}>
                  <NavItem
                    icon={folderIcon(folder.role)}
                    label={folder.name}
                    count={showCount}
                    bold={folder.role === "inbox" || folder.role === "junk"}
                    active={sameSource(current, source)}
                    href={sourcePath(source)}
                    drop={(ids) => moveTo(ids, folder.id, conversation, scope)}
                    menu={() => systemMenu(folder)}
                  />
                  {folder.role === "inbox" && (
                    <NavItem
                      icon="star"
                      label="Flagged"
                      count={counts.flagged}
                      active={sameSource(current, { kind: "view", id: "flagged" })}
                      href={sourcePath({ kind: "view", id: "flagged" })}
                    />
                  )}
                </Fragment>
              );
            })}
        </div>
        <button
          type="button"
          class="sidebar-more"
          aria-expanded={moreOpen}
          aria-controls="secondary-mail-navigation"
          onClick={() => setMoreOpen(!moreOpen)}
        >
          <Icon name={moreOpen ? "chevronUp" : "chevronDown"} size={20} />
          <span>{moreOpen ? "Fewer folders" : "More folders"}</span>
        </button>
        {moreOpen && (
          <div class="nav-section" id="secondary-mail-navigation">
            {system
              .filter((folder) => !["inbox", "sent", "drafts"].includes(folder.role ?? ""))
              .map((folder) => (
                <NavItem
                  key={folder.id}
                  icon={folderIcon(folder.role)}
                  label={folder.name}
                  count={folder.unread}
                  active={sameSource(current, { kind: "folder", id: folder.id })}
                  href={sourcePath({ kind: "folder", id: folder.id })}
                  drop={(ids) => moveTo(ids, folder.id, conversation, scope)}
                  menu={() => systemMenu(folder)}
                />
              ))}
            <NavItem
              icon="mail"
              label="All mail"
              active={sameSource(current, { kind: "view", id: "all" })}
              href={sourcePath({ kind: "view", id: "all" })}
            />
            {counts.vips > 0 && (
              <NavItem
                icon="crown"
                label="VIP"
                count={counts.vip}
                active={sameSource(current, { kind: "view", id: "vip" })}
                href={sourcePath({ kind: "view", id: "vip" })}
              />
            )}
            {counts.scheduled > 0 && (
              <NavItem
                icon="clock"
                label="Scheduled"
                count={counts.scheduled}
                active={sameSource(current, { kind: "view", id: "scheduled" })}
                href={sourcePath({ kind: "view", id: "scheduled" })}
              />
            )}
            {counts.snoozed > 0 && (
              <NavItem
                icon="alarm"
                label="Snoozed"
                count={counts.snoozed}
                active={sameSource(current, { kind: "view", id: "snoozed" })}
                href={sourcePath({ kind: "view", id: "snoozed" })}
              />
            )}
            <button type="button" class="sidebar-more" onClick={() => void addFolder()}>
              <Icon name="plus" size={20} />
              New folder
            </button>
            <button type="button" class="sidebar-more" onClick={() => void addLabel()}>
              <Icon name="plus" size={20} />
              New label
            </button>
          </div>
        )}
        {custom.length > 0 && (
          <div class="nav-heading" data-part="nav-heading">
            <button
              type="button"
              class="sidebar-more"
              aria-expanded={foldersOpen}
              onClick={() => setFoldersOpen(!foldersOpen)}
            >
              <Icon name={foldersOpen ? "chevronDown" : "chevronRight"} size={20} />
              <span>Folders</span>
            </button>
            <IconButton
              icon="plus"
              label="New folder"
              size={18}
              class="nav-more"
              onClick={() => addFolder()}
            />
          </div>
        )}
        {foldersOpen && (
          <div class="nav-section" data-part="nav-section">
            {custom.map(({ folder, depth }) => {
              const source: MailSource = { kind: "folder", id: folder.id };
              return (
                <NavItem
                  key={folder.id}
                  icon="folder"
                  label={folder.name}
                  count={folder.unread}
                  bold
                  depth={depth}
                  active={sameSource(current, source)}
                  href={sourcePath(source)}
                  drop={(ids) => moveTo(ids, folder.id, conversation, scope)}
                  menu={() => folderMenu(folder)}
                  actions={
                    <Menu
                      align="right"
                      trigger={({ toggle }) => (
                        <IconButton
                          icon="more"
                          label={`${folder.name} options`}
                          size={15}
                          class="nav-more"
                          onClick={toggle}
                        />
                      )}
                      items={() => folderMenu(folder)}
                    />
                  }
                />
              );
            })}
          </div>
        )}

        {labels.length > 0 && (
          <div class="nav-heading" data-part="nav-heading">
            <button
              type="button"
              class="sidebar-more"
              aria-expanded={labelsOpen}
              onClick={() => setLabelsOpen(!labelsOpen)}
            >
              <Icon name={labelsOpen ? "chevronDown" : "chevronRight"} size={20} />
              <span>Labels</span>
            </button>
            <IconButton
              icon="plus"
              label="New label"
              size={18}
              class="nav-more"
              onClick={addLabel}
            />
          </div>
        )}
        {labelsOpen && (
          <div class="nav-section" data-part="nav-section">
            {labels.map((label) => {
              const source: MailSource = { kind: "label", id: label.id };
              return (
                <NavItem
                  key={label.id}
                  icon="tag"
                  color={label.color}
                  label={label.name}
                  count={label.unread}
                  active={sameSource(current, source)}
                  href={sourcePath(source)}
                  drop={async (ids) => {
                    const change = { ids, threads: conversation, scopeFolderId: scope };
                    try {
                      await sendOrQueue("PATCH", "/messages", {
                        ...change,
                        addLabelIds: [label.id],
                      });
                    } catch (error) {
                      errorToast(error);
                      return;
                    }
                    mailChanged();
                    toast(`Labelled ${label.name}`, {
                      action: {
                        label: "Undo",
                        run: () => {
                          void sendOrQueue("PATCH", "/messages", {
                            ...change,
                            removeLabelIds: [label.id],
                          })
                            .then(mailChanged)
                            .catch(errorToast);
                        },
                      },
                    });
                  }}
                  menu={() => labelMenu(label)}
                  actions={
                    <Menu
                      align="right"
                      trigger={({ toggle }) => (
                        <IconButton
                          icon="more"
                          label={`${label.name} options`}
                          size={15}
                          class="nav-more"
                          onClick={toggle}
                        />
                      )}
                      items={() => labelMenu(label)}
                    />
                  }
                />
              );
            })}
          </div>
        )}

        {searches.length > 0 && (
          <>
            <div class="nav-heading" data-part="nav-heading">
              <button
                type="button"
                class="sidebar-more"
                aria-expanded={searchesOpen}
                onClick={() => setSearchesOpen(!searchesOpen)}
              >
                <Icon name={searchesOpen ? "chevronDown" : "chevronRight"} size={20} />
                <span>Saved searches</span>
              </button>
            </div>
            {searchesOpen && (
              <div class="nav-section" data-part="nav-section">
                {searches.map((search) => {
                  const source: MailSource = { kind: "search", query: search.query };
                  return (
                    <NavItem
                      key={search.id}
                      icon="search"
                      label={search.name}
                      active={current?.kind === "search" && current.query === search.query}
                      href={sourcePath(source)}
                      actions={
                        <Menu
                          align="right"
                          trigger={({ toggle }) => (
                            <IconButton
                              icon="more"
                              label={`${search.name} options`}
                              size={15}
                              class="nav-more"
                              onClick={toggle}
                            />
                          )}
                          items={[
                            {
                              label: "Rename",
                              icon: "pencil",
                              onSelect: async () => {
                                const name = window.prompt("Name", search.name);
                                if (!name?.trim()) return;
                                await api
                                  .patch(`/searches/${search.id}`, { name: name.trim() })
                                  .catch(errorToast);
                                await refreshSearches();
                              },
                            },
                            {
                              label: "Remove",
                              icon: "trash",
                              danger: true,
                              onSelect: async () => {
                                await api.del(`/searches/${search.id}`).catch(errorToast);
                                await refreshSearches();
                              },
                            },
                          ]}
                        />
                      }
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
        <div class="workspace-navigation" role="group" aria-label="Workspace navigation">
          <NavItem
            icon="book"
            label="Contacts"
            active={hash === "#/contacts"}
            href="#/contacts"
            extraClass="hide-mobile"
          />
          <NavItem
            icon="settings"
            label="Settings"
            active={hash.startsWith("#/settings")}
            href="#/settings/general"
            extraClass="hide-mobile"
          />

          <button
            type="button"
            class="sidebar-more"
            aria-expanded={workspaceOpen}
            aria-controls="cloudmail-navigation"
            onClick={() => setWorkspaceOpen(!workspaceOpen)}
          >
            <Icon name={workspaceOpen ? "chevronUp" : "chevronDown"} size={20} />
            <span>{workspaceOpen ? "Fewer tools" : "More tools"}</span>
          </button>
          {workspaceOpen && (
            <div id="cloudmail-navigation" class="nav-section">
              {appPages
                .filter((page) => !page.hidden)
                .map((page) => (
                  <NavItem
                    key={page.id}
                    icon={page.icon}
                    label={page.label}
                    active={
                      hash ===
                      (page.id === "files" ? mailHash("#/files") : (page.href ?? `#/${page.id}`))
                    }
                    href={page.id === "files" ? mailHash("#/files") : (page.href ?? `#/${page.id}`)}
                  />
                ))}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export const Sidebar = overridable("Sidebar", DefaultSidebar);
